import React from 'react';
import { useTranslation } from 'react-i18next';
import type { PublicFiche } from '../../types';
import { getJurisdictionStyle, getCaCity } from '../_utils';

interface CardHeaderProps {
  fiche:      PublicFiche;
  showScore?: boolean;
}

export const CardHeader = ({ fiche }: CardHeaderProps) => {
  const { t } = useTranslation();
  const jurStyle        = getJurisdictionStyle(fiche.jurisdiction);
  const isCA            = jurStyle.isCA;
  const isCC            = jurStyle.isCC;
  const isArretPrincipe = !!fiche.is_arret_principe;
  const caCity          = isCA ? getCaCity(fiche.location, fiche.location_label, fiche.slug) : '';
  const citedCount      = (fiche.citation_count ?? 0) > 0
    ? (fiche.citation_count ?? 0)
    : (fiche.cited_by_count ?? 0);
  const hasCitedBy      = citedCount > 0;

  const courtName = isCA && caCity
    ? `CA ${caCity}`
    : (fiche.location_label || fiche.jurisdiction || '');

  const metaParts = [fiche.chamber, fiche.decision_date, fiche.number || fiche.ecli].filter(Boolean);

  return (
    <div className="flex items-start justify-between gap-3 px-5 pt-4 pb-0">
      <div className="min-w-0">
        <div className="flex items-center gap-2 flex-wrap">
          {isCC && (
            <span className="text-[10px] font-bold px-1.5 py-0.5 rounded font-mono bg-sl-blue-l text-sl-blue">
              Cass.
            </span>
          )}
          {isCA && caCity && (
            <span className="text-[10px] font-bold px-1.5 py-0.5 rounded font-mono bg-sl-teal-l text-sl-teal">
              C.A.
            </span>
          )}
          <span className={`font-serif text-[15px] font-bold leading-tight ${isCA ? 'text-sl-teal' : 'text-sl-ink'}`}>
            {courtName}
            {fiche.location_label && fiche.chamber && (
              <span className="font-sans font-normal text-[13px] text-muted-foreground ml-1">
                , {fiche.chamber}
              </span>
            )}
          </span>
          {isArretPrincipe && (
            <span
              title={t('fiche.card.principle_title', { score: fiche.principe_score ?? '?' })}
              className="inline-flex items-center gap-1 text-[10px] font-semibold px-1.5 py-0.5 rounded bg-amber-50 text-amber-800 dark:bg-amber-900/20 dark:text-amber-300 border border-amber-300 dark:border-amber-700"
            >
              <svg width="9" height="9" viewBox="0 0 24 24" fill="currentColor" aria-hidden="true">
                <polygon points="12 2 15.09 8.26 22 9.27 17 14.14 18.18 21.02 12 17.77 5.82 21.02 7 14.14 2 9.27 8.91 8.26 12 2"/>
              </svg>
              {t('fiche.header.principle_badge')}
            </span>
          )}
        </div>
        <p className="text-[11px] text-muted-foreground mt-0.5 font-mono">
          {metaParts.join(' · ')}
        </p>
      </div>

      {hasCitedBy && (
        <span className={`flex-shrink-0 inline-flex items-center gap-1 text-[11px] font-medium px-2 py-0.5 rounded-full whitespace-nowrap ${
          citedCount >= 50
            ? 'bg-amber-100 text-amber-700 dark:bg-amber-900/30 dark:text-amber-400'
            : citedCount >= 10
            ? 'bg-yellow-100 text-yellow-700 dark:bg-yellow-900/30 dark:text-yellow-400'
            : 'bg-muted text-muted-foreground'
        }`}>
          <svg className="w-2.5 h-2.5" fill="none" stroke="currentColor" viewBox="0 0 24 24" strokeWidth={2} strokeLinecap="round">
            <path d="M10 13a5 5 0 0 0 7.54.54l3-3a5 5 0 0 0-7.07-7.07l-1.72 1.71"/>
            <path d="M14 11a5 5 0 0 0-7.54-.54l-3 3a5 5 0 0 0 7.07 7.07l1.71-1.71"/>
          </svg>
          {t('fiche.card.cited_times', { count: citedCount })}
        </span>
      )}
    </div>
  );
};